import React from "react";
import { alpha, Box, Button, Container, Paper, Typography, useTheme } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { SnackBarMessage } from "../../../modals/SnackBarMessage";
import { useSnackbarPosition } from "../../../shared/hooks/useSnackbarPosition";

interface Props {
  snackBarMessage: string;
  snackBarMode: "success" | "error" | "warning" | "info" | undefined;
  handleCloseSnackbar: () => void;
  onRetry: () => void;
}

export const GenerateReportError: React.FC<Props> = ({ snackBarMessage, snackBarMode, handleCloseSnackbar, onRetry }) => {
  const theme = useTheme();
  const snackbarPosition = useSnackbarPosition();

  return (
    <Container
      sx={{
        px: { xs: 1, sm: 2, md: 3 },
        minWidth: { xs: "200px", sm: "450px" },
        maxWidth: "100%",
        width: "100%",
        overflow: "auto",
        position: "relative",
      }}
    >
      <SnackBarMessage
        handleClose={handleCloseSnackbar}
        mode={snackBarMode}
        message={snackBarMessage}
        horizontalAnchor="center"
        verticalAnchor="top"
        sxOverrides={snackbarPosition}
      />
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, md: 8 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 3,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.02)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 56, color: "error.main" }} />
        </Box>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "error.main", mb: 1 }}>
            Error Loading Video Data
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 400, mx: "auto" }}>
            We couldn't load the video details. Please try again later or contact support if the issue persists.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={onRetry}
          startIcon={<RefreshIcon />}
          sx={{
            mt: 1,
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            px: 4,
            py: 1.2,
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
            "&:hover": { boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` },
          }}
        >
          Try Again
        </Button>
      </Paper>
    </Container>
  );
};
